/**
 * Checks on a graphic layered over a scene, read through luminary's composition introspection. The
 * composition is rasterized alone, so every pixel it leaves transparent is the scene beneath.
 *
 * A layered graphic may clear before its scene ends, so it is sampled through the scene rather than
 * judged on its last frame. Disqualifying, and so fed back to the designer: drawing nothing, painting
 * over so much of the frame that the scene beneath is hidden, and painting over the speaker's head.
 * The layout is recorded as the chart's is, for its rate to decide whether to act on it.
 */

import {
    type ChartComposition,
    type FrameSize,
    type LayoutFindings,
    measureLayout,
    NO_TIMELINE_PROBLEM,
} from 'js/src/agent/html-design/chart/chartChecks';
import type { ImageLike } from 'js/src/agent/html-design/lint/contrast';
import { MAX_PAINTED_SHARE, paintedShare } from 'js/src/agent/html-design/lint/overlayRules';

import type { FrameBox } from './speakerFaces';

export interface LayeredGraphicCheck {
    /** Why the graphic can't be used, for the designer to fix; null when it can. */
    problem: string | null;
    record: {
        /** The most of the frame it paints over at any sampled moment. */
        paintedShare: number;
        /** The most of any speaker's head it paints over at any sampled moment. */
        headShare: number;
        /** Measured at the sampled moment with the most to read. */
        layout: LayoutFindings | null;
    } | null;
}

const SAMPLE_PROGRESS = [0.2, 0.4, 0.6, 0.8, 0.995];
const RASTER_SCALE = 0.25;
// Even a translucent plate over a face hides the expression viewers watch.
const HEAD_PAINT_ALPHA = 64;
// Room for an edge of a plate or a stroke's glow to brush the padded head box.
const MAX_HEAD_SHARE = 0.05;

export const NOTHING_DRAWN_PROBLEM =
    'The graphic draws nothing at any point in the scene: every sampled frame is fully transparent. Check that its elements are on screen, visible and inside the frame by the time the narration reaches them.';

export async function checkLayeredGraphic(
    composition: ChartComposition,
    frame: FrameSize,
    heads: readonly FrameBox[],
): Promise<LayeredGraphicCheck> {
    if (!composition.timeline) {
        return { problem: NO_TIMELINE_PROBLEM, record: null };
    }
    let drawsAnything = false;
    let cover = { share: 0, progress: 0 };
    let head = { share: 0, progress: 0 };
    let layout: LayoutFindings | null = null;
    for (const progress of SAMPLE_PROGRESS) {
        const document = (await composition.seek(progress)) ?? composition.document;
        const measured = measureLayout(document, frame);
        if (!layout || measured.words > layout.words) {
            layout = measured;
        }
        const image = imageOf(await composition.rasterize(RASTER_SCALE));
        if (!image) {
            continue;
        }
        drawsAnything ||= hasAnyPaint(image);
        const share = paintedShare(image);
        if (share > cover.share) {
            cover = { share, progress };
        }
        const headShare = Math.max(0, ...heads.map((box) => shareOfBoxPainted(image, box, frame)));
        if (headShare > head.share) {
            head = { share: headShare, progress };
        }
    }
    const record = { paintedShare: cover.share, headShare: head.share, layout };
    if (!drawsAnything) {
        return { problem: NOTHING_DRAWN_PROBLEM, record };
    }
    if (cover.share > MAX_PAINTED_SHARE) {
        return {
            problem:
                `At ${percent(cover.progress)} of the scene the graphic paints over ${percent(cover.share)} ` +
                `of the frame, hiding the scene beneath it (at most ${percent(MAX_PAINTED_SHARE)}). Keep ` +
                '`html`, `body` and any full-frame container transparent, and keep the graphic to a band or ' +
                'a side of the frame.',
            record,
        };
    }
    if (head.share > MAX_HEAD_SHARE) {
        return {
            problem:
                `At ${percent(head.progress)} of the scene the graphic paints over the speaker's head ` +
                `(${heads.map(describeBox).join('; ')}). Keep every element, plates included, clear of it.`,
            record,
        };
    }
    return { problem: null, record };
}

export function describeBox({ x, y, width, height }: FrameBox): string {
    return `x ${x}–${x + width}, y ${y}–${y + height}`;
}

function percent(share: number): string {
    return `${Math.round(share * 100)}%`;
}

function imageOf(canvas: HTMLCanvasElement): ImageLike | null {
    const context = canvas.getContext('2d');
    if (!context || canvas.width === 0 || canvas.height === 0) {
        return null;
    }
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    return { data, width: canvas.width, height: canvas.height };
}

function hasAnyPaint({ data }: ImageLike): boolean {
    for (let index = 3; index < data.length; index += 4) {
        if (data[index] > 0) return true;
    }
    return false;
}

/** How much of a box, given in frame pixels, the image paints over. */
function shareOfBoxPainted(image: ImageLike, box: FrameBox, frame: FrameSize): number {
    const scaleX = image.width / frame.width;
    const scaleY = image.height / frame.height;
    const left = Math.max(0, Math.floor(box.x * scaleX));
    const right = Math.min(image.width, Math.ceil((box.x + box.width) * scaleX));
    const top = Math.max(0, Math.floor(box.y * scaleY));
    const bottom = Math.min(image.height, Math.ceil((box.y + box.height) * scaleY));
    let painted = 0;
    let total = 0;
    for (let y = top; y < bottom; y++) {
        for (let x = left; x < right; x++) {
            total++;
            if (image.data[(y * image.width + x) * 4 + 3] >= HEAD_PAINT_ALPHA) painted++;
        }
    }
    return total === 0 ? 0 : painted / total;
}
